import {useQuery, UseQueryOptions} from 'react-query'

import {
  api,
  queryClient,
  useMutationQuery,
  replaceQueryClientDataItem,
} from 'base'
import {showSuccessNotification} from 'common/helpers'
import {Pagination} from 'common/types'
import {UserBase, BrokerBase, User} from '.'

type UsersType = 'borrowers' | 'brokers' | 'team' | 'managers'

interface UseGetUsersParams {
  // type: UsersType
  // role: Role
  page: number
  search: string
  sort: string
  // managed?: boolean
}

export const useGetUsers = ({
  page,
  search,
  sort,
}: UseGetUsersParams) =>
  useQuery<Pagination<User>>(
    ['all-users', {page, search, sort}],
    async ({signal}) => {
      const {data} = await api.get('/user/list', {
        signal,
        params: {
          page,
          sort,
          filter: {search},
          paginate: true,
          perPage: 10,
        },
      })
      return data
    },
    {keepPreviousData: true}
  )

export const useGetTeam = (options?: UseQueryOptions<User[]>) =>
  useQuery<any>( // <User[]>(
    'team',
    async ({signal}) => {
      const {data} = await api.get('/user/list', {
        signal,
        params: {
          page: 1,
          sort: 'firstName',
          filter: {search:''},
          paginate: true,
          perPage: 10,
        },
      })
      return data
    },
    {keepPreviousData: true}
    // options
  )

export const useCreateNewUser = (
  onSuccessActions: (data?: any) => void
) => {
  return useMutationQuery<User, any, UserBase | BrokerBase>(
    'createNewUser',
    async (values) => {
      // console.log(values)
      const {data} = await api.post('/registration', values)
      // console.log('api data', data)
      return data.data
    },
    {
      onSuccess(data) {
        // console.log('success data', data)
        queryClient.invalidateQueries('all-users')
        onSuccessActions(data)
        showSuccessNotification(
          `New "${data.firstName} ${data.lastName}" has been created.`
        )
      },
    }
  )
}

export const useCreateUser = (
  type: UsersType,
  onSuccessActions: (data?: any) => void
) =>
  useMutationQuery<User, any, UserBase | BrokerBase>(
    ['createUser', type],
    async (values) => {
      const {data} = await api.post('/user/new', values)
      // console.log('api data', data)
      return data.data
    },
    {
      onSuccess(data) {
        // console.log('success data', data)
        queryClient.invalidateQueries('all-users')
        onSuccessActions(data)
        showSuccessNotification(
          `New "${data.firstName} ${data.lastName}" has been created.`
        )
      },
    }
  )

export const useUpdateUser = (uuid: string, onClose: () => void) =>
  useMutationQuery<User, any, Omit<UserBase, 'role' | 'email'>>(
    ['updateUser', uuid],
    async (values) => {
      const {data} = await api.post(`/user/${uuid}`, values)
      return data.data
    },
    {
      onSuccess(data) {
        replaceQueryClientDataItem('team', data)
        queryClient.invalidateQueries('team')
        showSuccessNotification(
          `Team member "${data.firstName} ${data.lastName}" has been updated.`
        )
        onClose()
      },
    }
  )

export const useUserUpdate = (uuid: string, onClose: () => void) =>
  useMutationQuery<User, any, Omit<UserBase, 'role' | 'email'>>(
    ['user-update', uuid],
    async (values) => {
      const {data} = await api.post(`/user/update/${uuid}`, values)
      return data.data
    },
    {
      onSuccess(data) {
        queryClient.invalidateQueries('all-users')
        showSuccessNotification(
          `User info has been updated.`
        )
        onClose()
      },
    }
  )

export const useGetAllUsers = (type: UsersType, roleName?: string, teamId?: string) => {
  return useQuery<User[]>(type, async ({signal}) => {
    const filter = (!!roleName && teamId) ? {
      teamId,
      roleName,
    } : (!!roleName) ? {
      roleName
    } : (!!teamId) ? {
      teamId
    } : undefined
    const option:any = {
      signal,
      params: {
        sort: 'firstName',
      },
    }
    if (!!filter) {
      option.params.filter = filter
    }

    const {data} = await api.get('/user/list', option)
    return data.data
  })
}

export const useGetAllUsersList = () => {
  return useQuery<User[]>(
    ['all-users', 'list'],
    async ({signal}) => {
    const {data} = await api.get('/user/list', {
      signal,
      params: {
        sort: 'firstName',
      },
    })
    // console.log(data)
    return data.data
  })
}

export const useGetBrokers = (search?: string) =>
  useQuery<any[]>(['brokers', {search}], async ({signal}) => {
    const {data} = await api.get('/brokerProfile/list', {
      signal,
      params: {
        filter: {search},
        sort: 'firstName',
      },
    })
    return data.data
  })
